import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle, Mail, Facebook, Instagram } from "lucide-react";
import { useLenis } from "lenis/react";
import Logo from "@/components/Logo";
import { WHATSAPP_URL, WHATSAPP_DISPLAY } from "@/lib/api";

const QUICK = [
  { label: "Services", target: "#solution", type: "scroll" },
  { label: "About", target: "#founder", type: "scroll" },
  { label: "Local Services", target: "#local-services", type: "scroll" },
  { label: "FAQ", target: "#faq", type: "scroll" },
  { label: "Contact", target: "#contact", type: "scroll" },
  { label: "Privacy Policy", target: "/privacy", type: "link" },
  { label: "Terms of Service", target: "/terms", type: "link" },
];

export default function Footer() {
  const lenis = useLenis();
  const go = (t) => {
    const el = document.querySelector(t);
    if (el && lenis) lenis.scrollTo(el, { offset: -80 });
  };

  return (
    <footer
      className="relative bg-paper text-ink pt-20 pb-10 border-t border-navy/10"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Logo dark={false} markSize={44} />
            <p className="mt-6 font-display italic text-ink/55 text-lg">
              “Your Eyes &amp; Ears on the Ground.”
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61583283967522", label: "Facebook" },
                { Icon: Instagram, href: "https://www.instagram.com/batearresearch/", label: "Instagram" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="w-10 h-10 flex items-center justify-center border border-navy/15 hover:border-gold hover:text-gold text-ink/70 transition-colors duration-300"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-5">
              Quick Links
            </div>
            <ul className="space-y-3">
              {QUICK.map((q) =>
                q.type === "link" ? (
                  <li key={q.target}>
                    <Link
                      to={q.target}
                      className="text-ink/65 hover:text-navy transition-colors text-sm"
                      data-testid={`footer-link-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {q.label}
                    </Link>
                  </li>
                ) : (
                  <li key={q.target}>
                    <button
                      onClick={() => go(q.target)}
                      className="text-ink/65 hover:text-navy transition-colors text-sm"
                      data-testid={`footer-link-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {q.label}
                    </button>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.25em] text-gold mb-5">
              Contact
            </div>
            <ul className="space-y-4 text-sm text-ink/75">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                Angetebu Road, Klottey Korle, Accra, Ghana
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-gold shrink-0" />
                <a href="tel:+233244153333" className="hover:text-navy">
                  Call Ghana: +233 24 415 3333
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle size={16} className="text-gold shrink-0" />
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-navy"
                >
                  WhatsApp: {WHATSAPP_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-gold shrink-0" />
                <a
                  href="mailto:info@batearghana.com"
                  className="hover:text-navy"
                >
                  info@batearghana.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-navy/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-ink/45">
          <p>© {new Date().getFullYear()} BAT Ear Research &amp; Investigations. All rights reserved.</p>
          <p className="max-w-lg sm:text-right">
            BAT Ear Research &amp; Investigations provides investigative and
            coordination services only and does not provide legal, financial, or
            notarial services.
          </p>
        </div>
      </div>
    </footer>
  );
}
